import React, { useEffect, useMemo, useState } from "react";
import { Modal, Nav, NavItem, NavLink, UncontrolledTooltip } from "reactstrap";
import classnames from "classnames";
import moment from "moment";
import {
  CASE_FILE_TYPE_PILL,
  caseFileTypePillBase,
  getCaseStatusPillTheme,
} from "./state";
import {
  getCaseFileOperationalWarnings,
  normalizeBagsFromFormAndApi,
} from "./caseFileOperationalWarnings";
import { CaseFileWarningsIcon } from "./CaseFileRemarksWarnings";
import { CustomerInfoDetails } from "../customerInfos/CustomerInfoDetails";
import {
  shouldShowBagFieldInCaseView,
  shouldShowClaimFields,
} from "./caseFileViewVisibility";

function joinPublicUrl(pathLike) {
  if (pathLike == null || pathLike === "") return "";
  const p = String(pathLike).trim();
  if (/^https?:\/\//i.test(p) || p.startsWith("data:") || p.startsWith("blob:")) return p;
  const base =
    (process.env.REACT_APP_STORAGE_TYPE === "azure"
      ? process.env.REACT_APP_STORAGE_URL
      : process.env.REACT_APP_API_PUBLIC_URL) || "";
  const rel = p.replace(/^\/+/, "");
  if (!base) return rel ? `/${rel}` : "";
  return `${base.replace(/\/+$/, "")}/${rel}`;
}

const styles = {
  headerTitle: { fontSize: "1.1rem", fontWeight: 600, margin: 0, color: "#1e293b" },
  summaryRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px 12px",
    padding: "12px 0 14px",
    borderBottom: "1px solid #e8ecf1",
    marginBottom: 16,
  },
  summaryBadges: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "8px 10px",
  },
  createdBy: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: "0.75rem",
    color: "#94a3b8",
    marginLeft: "auto",
    textAlign: "right",
    maxWidth: 220,
    lineHeight: 1,
    alignSelf: "center",
  },
  /** One consistent chip height for reference + status pills + meta text */
  refBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    boxSizing: "border-box",
    minHeight: 26,
    padding: "0 11px",
    borderRadius: 999,
    background: "#ecfdf5",
    color: "#047857",
    fontWeight: 700,
    fontSize: "0.6875rem",
    lineHeight: 1,
    letterSpacing: "0.02em",
    border: "1px solid #a7f3d0",
  },
  refBadgeIcon: {
    fontSize: "0.95rem",
    lineHeight: 1,
    display: "flex",
    alignItems: "center",
  },
  stripPill: (theme) => ({
    ...caseFileTypePillBase,
    display: "inline-flex",
    alignItems: "center",
    alignSelf: "center",
    justifyContent: "center",
    boxSizing: "border-box",
    minHeight: 26,
    lineHeight: 1,
    padding: "0 11px",
    fontSize: "0.6875rem",
    fontWeight: 600,
    letterSpacing: "0.02em",
    borderRadius: 999,
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    ...theme,
  }),
  bagsMeta: {
    display: "inline-flex",
    alignItems: "center",
    alignSelf: "center",
    minHeight: 26,
    fontSize: "0.8125rem",
    color: "#64748b",
    lineHeight: 1,
  },
  /** Icon-only pill when case is marked courtesy (summary row) */
  courtesyIconBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box",
    minWidth: 26,
    minHeight: 26,
    padding: "0 8px",
    borderRadius: 999,
    background: "#ecfdf5",
    color: "#047857",
    border: "1px solid #a7f3d0",
    lineHeight: 1,
  },
  sectionTitle: {
    fontSize: "0.78rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: "#64748b",
    margin: "22px 0 10px",
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  sectionBody: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 10,
    padding: "14px 16px",
  },
  kvGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
    gap: "12px 24px",
  },
  kvLabel: { fontSize: "0.72rem", fontWeight: 600, color: "#94a3b8", marginBottom: 2 },
  kvValue: { fontSize: "0.9rem", color: "#0f172a", wordBreak: "break-word" },
  remarksBox: {
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    padding: "12px 14px",
    minHeight: 48,
    fontSize: "0.9rem",
    color: "#334155",
    whiteSpace: "pre-wrap",
  },
  tabNav: { borderBottom: "1px solid #e2e8f0", marginBottom: 4 },
  tabLink: {
    borderRadius: "10px 10px 0 0",
    padding: "10px 16px",
    display: "flex",
    alignItems: "center",
    gap: 8,
    color: "#475569",
    cursor: "pointer",
  },
  tabLinkActive: {
    background: "linear-gradient(180deg, #e0f2fe 0%, #f0f9ff 100%)",
    color: "#0369a1",
    fontWeight: 600,
  },
  tabIcon: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.85rem",
    border: "2px solid #7dd3fc",
    background: "#fff",
    color: "#0369a1",
  },
  tabIconActive: { background: "#0369a1", color: "#fff", borderColor: "#0369a1" },
  bagCard: {
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    padding: 16,
    marginBottom: 14,
    background: "#fff",
    boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
  },
  noteCard: {
    display: "grid",
    gridTemplateColumns: "auto 1fr auto",
    gap: 12,
    padding: "12px 14px",
    marginBottom: 10,
    background: "#f1f5f9",
    borderRadius: 10,
    border: "1px solid #e2e8f0",
  },
  noteIdx: { fontWeight: 700, color: "#94a3b8", minWidth: 24, fontSize: "0.85rem" },
  attachmentThumb: { maxWidth: 120, maxHeight: 90, borderRadius: 8, border: "1px solid #e2e8f0" },
};

function KV({ label, value }) {
  const v = value != null && String(value).trim() !== "" ? value : "—";
  return (
    <div>
      <div style={styles.kvLabel}>{label}</div>
      <div style={styles.kvValue}>{v}</div>
    </div>
  );
}

function Section({ icon, title, children }) {
  return (
    <div>
      <div style={styles.sectionTitle}>
        {icon ? <i className={icon} style={{ fontSize: "1rem", color: "#0ea5e9" }} /> : null}
        {title}
      </div>
      <div style={styles.sectionBody}>{children}</div>
    </div>
  );
}

function formatDate(d, fmt = "DD-MMM-YYYY") {
  if (!d) return "—";
  const m = moment(d);
  return m.isValid() ? m.format(fmt) : "—";
}

export function CaseFileViewModal({ isOpen, toggle, row: caseFile, footerActions = null }) {
  const [activeTab, setActiveTab] = useState("details");

  useEffect(() => {
    if (isOpen) setActiveTab("details");
  }, [isOpen, caseFile?.id]);

  const warnings = useMemo(() => {
    if (!caseFile) return [];
    return getCaseFileOperationalWarnings({
      type: caseFile.type,
      departureDate: caseFile.departure_date,
      flightDate: caseFile.flight_date,
      closed: caseFile.closed,
      caseStatus: caseFile.case_status,
      bags: normalizeBagsFromFormAndApi(null, caseFile.bags),
    });
  }, [caseFile]);

  const typePill =
    CASE_FILE_TYPE_PILL[caseFile?.type] || {
      background: "#f3f4f6",
      color: "#6b7280",
      border: "1px solid #e5e7eb",
    };
  const statusPill = getCaseStatusPillTheme(caseFile?.case_status);

  if (!caseFile) return null;

  const isCourtesy = Boolean(caseFile.courtesy);

  const bags = Array.isArray(caseFile.bags) ? caseFile.bags : [];
  const notes = Array.isArray(caseFile.notes) ? caseFile.notes : [];
  const attachments = Array.isArray(caseFile.attachments) ? caseFile.attachments : [];
  const mailLogs = Array.isArray(caseFile.case_mail_logs) ? caseFile.case_mail_logs : [];

  const renderBagField = (label, value, bag) => {
    if (label === "Bag Status") return null;
    if (!shouldShowBagFieldInCaseView(label, bag, caseFile.type)) return null;
    let display = value;
    if (label === "Delivery Signature" && value) {
      display = (
        <img
          src={joinPublicUrl(value)}
          alt="Signature"
          style={{ maxWidth: 280, maxHeight: 160, borderRadius: 6, border: "1px solid #e2e8f0" }}
        />
      );
    } else if (label === "Receive Date" || label === "Forward Date") {
      display = formatDate(value);
    } else if (label === "Deliver Date") {
      display = value ? moment(value).format("HH:mm | DD-MMM-YYYY") : "—";
    } else if (label === "Damage Bag Receive Date") {
      display = formatDate(value);
    }
    return <KV key={label} label={label} value={display} />;
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" backdrop="static" scrollable id="case-file-view-modal">
      <div className="modal-header" style={{ borderBottom: "none", paddingBottom: 8 }}>
        <h5 style={styles.headerTitle}>Case file</h5>
        <button type="button" className="close" onClick={toggle} aria-label="Close">
          <span aria-hidden="true">&times;</span>
        </button>
      </div>

      <div className="modal-body pt-0" style={{ paddingTop: 0 }}>
        <div style={styles.tabNav}>
          <Nav tabs className="nav-tabs-custom">
            <NavItem>
              <NavLink
                href="#"
                tag="a"
                className={classnames({ active: activeTab === "details" })}
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab("details");
                }}
                style={{
                  ...styles.tabLink,
                  ...(activeTab === "details" ? styles.tabLinkActive : {}),
                }}
              >
                <span style={{ ...styles.tabIcon, ...(activeTab === "details" ? styles.tabIconActive : {}) }}>
                  <i className="mdi mdi-eye" />
                </span>
                Details
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                href="#"
                tag="a"
                className={classnames({ active: activeTab === "bags" })}
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab("bags");
                }}
                style={{
                  ...styles.tabLink,
                  ...(activeTab === "bags" ? styles.tabLinkActive : {}),
                }}
              >
                <span style={{ ...styles.tabIcon, ...(activeTab === "bags" ? styles.tabIconActive : {}) }}>
                  <i className="mdi mdi-bag-suitcase" />
                </span>
                Bags ({bags.length})
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                href="#"
                tag="a"
                className={classnames({ active: activeTab === "notes" })}
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab("notes");
                }}
                style={{
                  ...styles.tabLink,
                  ...(activeTab === "notes" ? styles.tabLinkActive : {}),
                }}
              >
                <span style={{ ...styles.tabIcon, ...(activeTab === "notes" ? styles.tabIconActive : {}) }}>
                  <i className="mdi mdi-history" />
                </span>
                Notes ({notes.length})
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                href="#"
                tag="a"
                className={classnames({ active: activeTab === "attachments" })}
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab("attachments");
                }}
                style={{
                  ...styles.tabLink,
                  ...(activeTab === "attachments" ? styles.tabLinkActive : {}),
                }}
              >
                <span
                  style={{ ...styles.tabIcon, ...(activeTab === "attachments" ? styles.tabIconActive : {}) }}
                >
                  <i className="mdi mdi-paperclip" />
                </span>
                Files ({attachments.length})
              </NavLink>
            </NavItem>
            {caseFile.customer_info ? (
              <NavItem>
                <NavLink
                  href="#"
                  tag="a"
                  className={classnames({ active: activeTab === "customerInfo" })}
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveTab("customerInfo");
                  }}
                  style={{
                    ...styles.tabLink,
                    ...(activeTab === "customerInfo" ? styles.tabLinkActive : {}),
                  }}
                >
                  <span
                    style={{ ...styles.tabIcon, ...(activeTab === "customerInfo" ? styles.tabIconActive : {}) }}
                  >
                    <i className="mdi mdi-card-account-details-outline" />
                  </span>
                  Customer Info
                </NavLink>
              </NavItem>
            ) : null}
            <NavItem>
              <NavLink
                href="#"
                tag="a"
                className={classnames({ active: activeTab === "mailed" })}
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab("mailed");
                }}
                style={{
                  ...styles.tabLink,
                  ...(activeTab === "mailed" ? styles.tabLinkActive : {}),
                }}
              >
                <span
                  style={{ ...styles.tabIcon, ...(activeTab === "mailed" ? styles.tabIconActive : {}) }}
                >
                  <i className="mdi mdi-email-check-outline" />
                </span>
                Mailed ({mailLogs.length})
              </NavLink>
            </NavItem>
          </Nav>
        </div>

        <div style={styles.summaryRow}>
          <div style={styles.summaryBadges}>
            <span style={styles.refBadge}>
              <i className="mdi mdi-file-document-outline" style={styles.refBadgeIcon} aria-hidden />
              {caseFile.primary_reference || caseFile.reference || "—"}
            </span>
            <span style={styles.stripPill(typePill)}>{caseFile.type || "—"}</span>
            <span style={styles.stripPill(statusPill)}>{caseFile.case_status || "—"}</span>
            {caseFile.closed ? (
              <span
                style={styles.stripPill({
                  background: "#fef2f2",
                  color: "#b91c1c",
                  border: "1px solid #fecaca",
                })}
              >
                Closed
              </span>
            ) : (
              <span
                style={styles.stripPill({
                  background: "#eff6ff",
                  color: "#1d4ed8",
                  border: "1px solid #bfdbfe",
                })}
              >
                Open
              </span>
            )}
            {isCourtesy ? (
              <>
                <span
                  id={`casefile-view-courtesy-${caseFile.id}`}
                  style={styles.courtesyIconBadge}
                  aria-label="Courtesy"
                >
                  <i className="mdi mdi-handshake-outline" style={{ fontSize: "1.05rem", lineHeight: 1 }} aria-hidden />
                </span>
                <UncontrolledTooltip placement="top" target={`casefile-view-courtesy-${caseFile.id}`}>
                  Courtesy
                </UncontrolledTooltip>
              </>
            ) : null}
            {caseFile.number_of_bags != null && (
              <span style={styles.bagsMeta}>
                Bags: {caseFile.number_of_received_bags ?? 0}/{caseFile.number_of_bags} received
              </span>
            )}
          </div>
          {caseFile.user?.name ? (
            <div style={styles.createdBy} title="User who created this case file">
              <i className="mdi mdi-account-outline" style={{ fontSize: "1rem", lineHeight: 1 }} aria-hidden />
              <span>Created by {caseFile.user.name}</span>
            </div>
          ) : null}
        </div>

        {activeTab === "details" && (
          <div>
            <Section icon="mdi mdi-airplane" title="Flight & reference">
              <div style={styles.kvGrid}>
                <KV label="Reference" value={caseFile.reference} />
                <KV label="File reference" value={caseFile.file_reference} />
                <KV label="Flight" value={caseFile.flight} />
                <KV label="Flight date" value={formatDate(caseFile.flight_date)} />
                {caseFile.type === "DPR" && (
                  <KV label="Damage bag claim" value={caseFile.damage_bag_claim?.name} />
                )}
              </div>
            </Section>

            <Section icon="mdi mdi-account" title="Passenger & location">
              <div style={styles.kvGrid}>
                <KV label="First name" value={caseFile.first_name || "—"} />
                <KV label="Last name" value={caseFile.last_name || "—"} />
                <KV
                  label="Address"
                  value={
                    caseFile.address?.name ??
                    (typeof caseFile.address === "string" ? caseFile.address : null)
                  }
                />
                <KV
                  label="Passenger departure"
                  value={formatDate(caseFile.departure_date)}
                />
                {shouldShowClaimFields(caseFile.type) && (
                  <>
                    <KV label="Claim type" value={caseFile.claim_type?.name} />
                    <KV label="Claimed item" value={caseFile.claimed_item} />
                  </>
                )}
              </div>
            </Section>

            <div>
              <div style={styles.sectionTitle}>
                <CaseFileWarningsIcon warnings={warnings} />
                <i className="mdi mdi-comment-text-outline" style={{ fontSize: "1rem", color: "#0ea5e9" }} />
                Remarks
              </div>
              <div style={styles.remarksBox}>
                {caseFile.remarks != null && String(caseFile.remarks).trim() !== ""
                  ? caseFile.remarks
                  : "—"}
              </div>
            </div>
          </div>
        )}

        {activeTab === "bags" && (
          <div>
            {bags.length === 0 ? (
              <p className="text-muted mb-0">No bags on this case.</p>
            ) : (
              bags.map((bag, idx) => (
                <div key={bag.id ?? idx} style={styles.bagCard}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: 12,
                      flexWrap: "wrap",
                      gap: 8,
                    }}
                  >
                    <strong style={{ fontSize: "1rem", color: "#0f172a" }}>Bag {idx + 1}</strong>
                    <span
                      style={{
                        ...caseFileTypePillBase,
                        ...getCaseStatusPillTheme(bagStatusName(bag)),
                      }}
                    >
                      {bag.bag_status?.name ?? bag.bagStatus?.name ?? "—"}
                    </span>
                  </div>
                  <div style={styles.kvGrid}>
                    {renderBagField("Tag Number", bag.tag_number, bag)}
                    {renderBagField("Color type", bag.color_type, bag)}
                    {renderBagField(
                      "Arriving Flight",
                      bag.location_history,
                      bag,
                    )}
                    {renderBagField("Receive Date", bag.receive_date, bag)}
                    {renderBagField("Received Flight", bag.received_flight, bag)}
                    {renderBagField(
                      "Received Weight (kg)",
                      bag.received_weight != null && bag.received_weight !== "" ? String(bag.received_weight) : null,
                      bag,
                    )}
                    {renderBagField("Forward Date", bag.forward_date, bag)}
                    {renderBagField("Forward Flight", bag.forward_flight, bag)}
                    {renderBagField(
                      "Forward Type",
                      bag.forward_type?.name ?? bag.forwardType?.name,
                      bag,
                    )}
                    {renderBagField("Rush Tag Number", bag.rush_tag_number, bag)}
                    {renderBagField("Deliver Date", bag.deliver_date, bag)}
                    {renderBagField("Delivery Name", bag.delivery_name, bag)}
                    {renderBagField("Delivery Contact", bag.delivery_contact, bag)}
                    {renderBagField("Delivery Identity", bag.delivery_identity, bag)}
                    {renderBagField("Delivery Condition", bag.delivery_condition, bag)}
                    {renderBagField("Delivery Signature", bag.delivery_signature, bag)}
                    {renderBagField(
                      "Damage Bag Receive Date",
                      bag.damage_bag_receive_date,
                      bag,
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === "notes" && (
          <div>
            {notes.length === 0 ? (
              <p className="text-muted mb-0">No notes yet.</p>
            ) : (
              notes.map((note, idx) => (
                <div
                  key={note.id ?? idx}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "auto 1fr",
                    gap: "12px 16px",
                    padding: "14px 16px",
                    marginBottom: 10,
                    background: "#f8fafc",
                    borderRadius: 10,
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <div style={styles.noteIdx}>{notes.length - idx}</div>
                  <div>
                    <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.8rem" }}>
                      {note.created_at
                        ? moment(note.created_at).format("Do MMM YYYY | HH:mm")
                        : "—"}
                    </div>
                    <div style={{ fontSize: "0.75rem", color: "#64748b", marginTop: 4 }}>
                      {note.user?.name ?? note.user?.rcno ?? "—"}
                    </div>
                    <div style={{ marginTop: 10, fontSize: "0.9rem", color: "#334155", lineHeight: 1.5 }}>
                      {note.note ?? note.body ?? "—"}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === "attachments" && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
            {attachments.length === 0 ? (
              <p className="text-muted mb-0">No attachments.</p>
            ) : (
              attachments.map((att, k) => {
                const path = att.path || "";
                const isPdf = path.toLowerCase().endsWith(".pdf") || att.name?.toLowerCase()?.endsWith(".pdf");
                const url = joinPublicUrl(path);
                return (
                  <div key={att.id ?? k} style={{ textAlign: "center", maxWidth: 140 }}>
                    {isPdf ? (
                      <a href={url} target="_blank" rel="noopener noreferrer" className="d-block">
                        <i className="mdi mdi-file-pdf-box" style={{ fontSize: 48, color: "#dc2626" }} />
                      </a>
                    ) : (
                      <a href={url} target="_blank" rel="noopener noreferrer">
                        <img src={url} alt={att.name} style={styles.attachmentThumb} />
                      </a>
                    )}
                    <div style={{ fontSize: "0.75rem", marginTop: 6, wordBreak: "break-all" }}>
                      {att.name || "File"}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {activeTab === "customerInfo" && caseFile.customer_info && (
          <CustomerInfoDetails info={caseFile.customer_info} hideDuplicates />
        )}

        {activeTab === "mailed" && (
          <div>
            {mailLogs.length === 0 ? (
              <p className="text-muted mb-0">No emails sent yet.</p>
            ) : (
              mailLogs.map((log, idx) => (
                <div
                  key={log.id ?? idx}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "auto 1fr",
                    gap: "12px 16px",
                    padding: "14px 16px",
                    marginBottom: 10,
                    background: "#f0f9ff",
                    borderRadius: 10,
                    border: "1px solid #bae6fd",
                  }}
                >
                  <div style={{
                    width: 28, height: 28, borderRadius: "50%",
                    background: "#0284c7", color: "#fff",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 14, flexShrink: 0,
                  }}>
                    <i className="mdi mdi-email-outline" />
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.8rem" }}>
                      {log.created_at ? moment(log.created_at).format("Do MMM YYYY | HH:mm") : "—"}
                    </div>
                    <div style={{ fontSize: "0.75rem", color: "#64748b", marginTop: 4 }}>
                      {log.user?.name ?? "—"}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      <div className="modal-footer d-flex flex-wrap align-items-center w-100" style={{ gap: 8 }}>
        {footerActions ? (
          <div className="d-flex flex-wrap align-items-center" style={{ gap: 4 }}>
            {footerActions}
          </div>
        ) : null}
        <button
          type="button"
          className={`btn btn-secondary waves-effect ${footerActions ? 'ml-auto' : ''}`}
          onClick={toggle}
        >
          Close
        </button>
      </div>
    </Modal>
  );
}

function bagStatusName(bag) {
  return bag?.bag_status?.name ?? bag?.bagStatus?.name ?? "";
}
